import React, { useState, useEffect } from 'react'
import { Modal, AutoComplete } from "antd";
import { addAssessment } from '../../../../services/assessment';
import { useRecoilValue, useRecoilState } from 'recoil';
import { assessmentAtom, sessionTimingAtom } from "../../../../state/assessment";
import { MODULE_6_INSTANCES, getModule6Instance } from '../../../../data/module6Instances';
import Close from "../../../../resources/imgs/modal/close.svg";
import { FaChevronRight } from "react-icons/fa";
import Progress from "../../progress";
import HelpBtn from "../../../../resources/imgs/assessment/helpBtn.svg";
// Import all character images for dynamic usage
import Joey1 from "../../../../resources/imgs/assessment/characters/joey1.png";
import Karl1 from "../../../../resources/imgs/assessment/characters/karl1.png";
import Manuel1 from "../../../../resources/imgs/assessment/characters/manuel1.png";
import Pamela1 from "../../../../resources/imgs/assessment/characters/pamela1.png";
import Peter1 from "../../../../resources/imgs/assessment/characters/peter1.png";
import Reina1 from "../../../../resources/imgs/assessment/characters/reina1.png";
import Roy1 from "../../../../resources/imgs/assessment/characters/roy1.png";
import Samantha1 from "../../../../resources/imgs/assessment/characters/samantha1.png";
import Trish1 from "../../../../resources/imgs/assessment/characters/trish1.png";

// Default character image mapping for fallback
const defaultCharacterImages = {
  "Manuel": Manuel1,
  "Pamela": Pamela1,
  "Trish": Trish1,
  "Karl": Karl1,
  "Joey": Joey1,
  "Reina": Reina1,
  "Peter": Peter1,
  "Roy": Roy1,
  "Samantha": Samantha1
};

export default function Index(props) {
  const assessment = useRecoilValue(assessmentAtom);
  
  // Create character image mapping from cardData or use defaults
  const characterImages = React.useMemo(() => {
    if (props.cardData && props.cardData.length > 0) {
      const mapping = {};
      props.cardData.forEach(card => {
        // Use otherImg (from Module 1-0) or fall back to default
        mapping[card.name] = card.otherImg || defaultCharacterImages[card.name];
      });
      console.log('🔍 Module6: Using dynamic character mapping:', Object.keys(mapping));
      return mapping;
    }
    console.log('⚠️ Module6: No cardData, using default character mapping');
    return defaultCharacterImages;
  }, [props.cardData]);
  
  // Get difficulty from props or default to 5
  const difficulty = props.difficulty || 5;
  const instanceData = props.instance || getModule6Instance(difficulty);
  
  // Create name mapping from hardcoded names to dynamic character names
  const nameMapping = React.useMemo(() => {
    console.log('🔍 Module6 nameMapping: cardData received:', props.cardData?.map(c => c.name));
    
    if (props.cardData && props.cardData.length >= 6 && instanceData) {
      const mapping = {};
      
      // Get unique speakers from this specific instance
      const uniqueSpeakers = new Set();
      
      // Check both possible data structures (blanks and contextLines)
      if (instanceData.blanks) {
        instanceData.blanks.forEach(blank => {
          if (blank.speaker) uniqueSpeakers.add(blank.speaker);
        });
      }
      if (instanceData.contextLines) {
        instanceData.contextLines.forEach(line => {
          if (line.speaker) uniqueSpeakers.add(line.speaker);
        });
      }
      if (instanceData.dialogue) {
        instanceData.dialogue.forEach(line => {
          if (line.speaker) uniqueSpeakers.add(line.speaker);
        });
      }
      
      const speakersArray = Array.from(uniqueSpeakers);
      console.log('🔍 Module6: Unique speakers in instance:', speakersArray);
      
      // Get the selected character names from cardData
      const selectedNames = props.cardData.slice(0, 6).map(card => card.name);
      console.log('🔍 Module6: Available character names from cardData:', selectedNames);
      
      // Filter out names that are already in the original speakers to avoid self-mapping
      const availableReplacements = selectedNames.filter(name => !speakersArray.includes(name));
      console.log('🔍 Module6: Available replacements (excluding originals):', availableReplacements);
      
      // If we filtered out too many names, add back some from selectedNames
      if (availableReplacements.length < speakersArray.length) {
        // Add names that aren't in speakers but prioritize non-duplicates
        const additionalNames = selectedNames.filter(name => !availableReplacements.includes(name));
        availableReplacements.push(...additionalNames);
      }
      
      // Randomly shuffle the available replacement names
      const shuffledNames = [...availableReplacements].sort(() => Math.random() - 0.5);
      console.log('🔍 Module6: Shuffled replacement names:', shuffledNames);
      
      // Map each unique speaker to a different character from cardData
      speakersArray.forEach((speaker, index) => {
        // Try to avoid mapping a name to itself
        let replacement = shuffledNames[index % shuffledNames.length];
        
        // If we're about to map a name to itself, try to find an alternative
        if (replacement === speaker && shuffledNames.length > 1) {
          // Find a different name from the shuffled list
          const alternativeIndex = (index + 1) % shuffledNames.length;
          replacement = shuffledNames[alternativeIndex];
        }
        
        mapping[speaker] = replacement;
      });
      
      console.log('🔍 Module6: Name mapping for this instance:', mapping);
      console.log('🔍 Module6: Checking for duplicates:', Object.values(mapping));
      return mapping;
    }
    // If no cardData, return identity mapping (names stay the same)
    return {};
  }, [props.cardData, instanceData]);
  
  // Apply name mapping to instance data
  const mappedInstanceData = React.useMemo(() => {
    if (Object.keys(nameMapping).length === 0) {
      return instanceData;
    }
    
    // Deep clone the instance data and replace speaker names
    const mapped = JSON.parse(JSON.stringify(instanceData));
    if (mapped.blanks) {
      mapped.blanks = mapped.blanks.map(blank => ({
        ...blank,
        speaker: nameMapping[blank.speaker] || blank.speaker
      }));
    }
    if (mapped.contextLines) {
      mapped.contextLines = mapped.contextLines.map(line => ({
        ...line,
        speaker: nameMapping[line.speaker] || line.speaker
      }));
    }
    return mapped;
  }, [instanceData, nameMapping]);
  
  const [currentInstance, setCurrentInstance] = useState(mappedInstanceData);
  const [sessionTiming, setSessionTiming] = useRecoilState(sessionTimingAtom);
  const [instanceStartTime, setInstanceStartTime] = useState(null);

  // Update instance when props change (for adaptive mode)
  useEffect(() => {
    if (props.instance) {
      // Apply name mapping to the new instance
      const mapped = JSON.parse(JSON.stringify(props.instance));
      if (mapped.blanks && Object.keys(nameMapping).length > 0) {
        mapped.blanks = mapped.blanks.map(blank => ({
          ...blank,
          speaker: nameMapping[blank.speaker] || blank.speaker
        }));
      }
      if (mapped.contextLines && Object.keys(nameMapping).length > 0) {
        mapped.contextLines = mapped.contextLines.map(line => ({
          ...line,
          speaker: nameMapping[line.speaker] || line.speaker
        }));
      }
      setCurrentInstance(mapped);

      // Initialize session start time if not already set
      const now = Date.now();
      if (!sessionTiming.sessionStartTime) {
        setSessionTiming(prev => ({
          ...prev,
          sessionStartTime: now
        }));
      }

      // Record instance start time
      setInstanceStartTime(now);
      console.log(`⏱️ Module 6 Instance ${props.instanceNumber} started at ${now - (sessionTiming.sessionStartTime || now)}ms from session start`);
    }
  }, [props.instance, nameMapping, sessionTiming.sessionStartTime, props.instanceNumber]);
  const [options, setOptions] = useState([]);
  const [values, setValues] = useState({});
  const [open, setOpen] = useState(false);
  const [flag, setFlag] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [feedback, setFeedback] = useState({});
  
  const [words, setWords] = useState([]);
  
  // Generate word bank from instance data
  const generateWordBank = (instance) => {
    const allWords = [];
    const blanks = instance.dialogue ? 
      instance.dialogue.filter(line => line.hasBlank) : 
      instance.blanks || [];
    
    blanks.forEach(blank => {
      blank.wordOptions.forEach(word => {
        if (!allWords.includes(word)) {
          allWords.push(word);
        }
      });
    });
    // Shuffle the word bank
    return allWords.sort(() => Math.random() - 0.5);
  };

  useEffect(() => {
    // Event handler for resizing
    const handleResize = () => {
      if (window.innerWidth <= 1024) {
        setFlag(true)
      } else {
        setFlag(false);
      }
    };

    // Adding the resize event listener when the component mounts
    window.addEventListener('resize', handleResize);
    handleResize(); // Check initial size

    // Cleaning up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    // Get blanks from dialogue (for backwards compatibility, check both structures)
    const blanks = currentInstance.dialogue ? 
      currentInstance.dialogue.filter(line => line.hasBlank) : 
      currentInstance.blanks || [];
    
    // Initialize values object for blanks
    const initialValues = {};
    blanks.forEach((blank, index) => {
      initialValues[`value${index + 1}`] = "";
    });
    setValues(initialValues);
    
    // Reset form state for new instance
    setSubmitted(false);
    setFeedback({});
    setCorrectCount(0);
    
    // Generate word bank when instance changes
    setWords(generateWordBank(currentInstance));
  }, [currentInstance]);

  const showModal = () => {
    setOpen(true);
  };

  const hideModal = () => {
    setOpen(false);
  };

  const handleSearch = (value, blankIndex) => {
    setOptions(() => {
      return words.filter(word =>
        word.toLowerCase().includes(value.toLowerCase())
      ).map(val => ({ value: val }))
    });
  };

  const handleChange = (value, blankIndex) => {
    setValues(prev => ({
      ...prev,
      [`value${blankIndex + 1}`]: value
    }));
  };

  const checkAnswers = () => {
    let correct = 0;
    const newFeedback = {};
    
    const blanks = currentInstance.dialogue ? 
      currentInstance.dialogue.filter(line => line.hasBlank) : 
      currentInstance.blanks || [];
    
    blanks.forEach((blank, index) => {
      const userAnswer = values[`value${index + 1}`];
      const isCorrect = userAnswer?.toLowerCase() === blank.correctAnswer.toLowerCase();
      newFeedback[`blank${index + 1}`] = isCorrect;
      if (isCorrect) correct++;
    });
    
    setFeedback(newFeedback);
    setCorrectCount(correct);
    setSubmitted(true);
    
    // Calculate percentage and determine if user passes (80% threshold)
    const percentage = correct / blanks.length;
    const passes = percentage >= 0.8;
    const points = Math.round(percentage * currentInstance.difficulty);

    console.log(`🎯 Module 6 scoring: correct=${correct}, total=${blanks.length}, percentage=${percentage}, passes=${passes}, points=${points}, difficulty=${currentInstance.difficulty}`);

    // Record trial timing data
    const now = Date.now();
    const sessionStart = sessionTiming.sessionStartTime || now;
    const trialStart = instanceStartTime || now;

    const trialData = {
      module_id: '6',
      instance_id: currentInstance?.instanceId?.toString() || props.instanceNumber?.toString(),
      timestamp_start_ms: trialStart - sessionStart,
      timestamp_response_ms: now - sessionStart,
      response_latency_ms: now - trialStart,
      is_correct: passes,
      error_type: passes ? 'none' : 'accuracy',
      difficulty_level: currentInstance.difficulty,
      trial_number: props.instanceNumber
    };

    console.log('⏱️ Module 6 trial data:', trialData);

    // Add to session timing atom
    setSessionTiming(prev => ({
      ...prev,
      trialData: [...prev.trialData, trialData]
    }));

    // Call completion handler
    if (props.nextPage) {
      setTimeout(() => {
        props.nextPage({
          difficulty: currentInstance.difficulty,
          correctAnswers: correct,
          totalQuestions: blanks.length,
          isCorrect: passes,
          percentage: percentage,
          points: points,
          trialData
        });
      }, 3000);
    }
  };

  const nextStep = async () => {
    // Check if all fields are filled
    let allFilled = true;
    currentInstance.blanks.forEach((blank, index) => {
      if (!values[`value${index + 1}`]) {
        allFilled = false;
      }
    });

    if (!allFilled) {
      alert("Please fill in all blanks");
      return;
    }

    checkAnswers();
  };

  // Function to render dialogue with blanks and context lines
  const renderDialogue = () => {
    const blanks = currentInstance.blanks || [];
    const contextLines = currentInstance.contextLines || [];
    const dialogueElements = [];
    const processedSentences = new Set();
    
    // Render blank lines, grouping multiple blanks in same sentence
    blanks.forEach((blank, index) => {
      // Skip if we already processed this sentence
      if (processedSentences.has(blank.sentenceWithBlank)) {
        return;
      }
      
      processedSentences.add(blank.sentenceWithBlank);
      
      // Find all blanks in this sentence
      const blanksInSentence = blanks.filter(b => b.sentenceWithBlank === blank.sentenceWithBlank);
      
      // Replace all blanks in the sentence with input fields
      let sentenceContent = blank.sentenceWithBlank;
      const blankInputs = [];
      
      blanksInSentence.forEach((blankItem, blankIdx) => {
        const blankInput = (
          <AutoComplete
            key={`blank-${blankItem.blankIndex}`}
            style={{ 
              width: 100,
              borderColor: submitted ? (feedback[`blank${blankItem.blankIndex}`] ? 'green' : 'red') : undefined
            }}
            value={values[`value${blankItem.blankIndex}`]}
            onChange={(value) => handleChange(value, blankItem.blankIndex - 1)}
            onSearch={(value) => handleSearch(value, blankItem.blankIndex - 1)}
            options={options}
            status={submitted ? (feedback[`blank${blankItem.blankIndex}`] ? 'success' : 'error') : undefined}
            disabled={submitted}
            autoComplete="off"
          />
        );
        blankInputs.push(blankInput);
      });
      
      // Split sentence by blanks and intersperse with inputs
      const parts = sentenceContent.split(/\[BLANK \d+\]/);
      const sentenceWithBlanks = [];
      
      parts.forEach((part, partIdx) => {
        sentenceWithBlanks.push(part);
        if (partIdx < blankInputs.length) {
          sentenceWithBlanks.push(blankInputs[partIdx]);
        }
      });

      // Get the mapped character name for this speaker
      const mappedSpeaker = nameMapping[blank.speaker] || blank.speaker;
      
      dialogueElements.push(
        <div key={`dialogue-${blank.speaker}-${index}`} className='module6-item-container'>
          <img src={characterImages[mappedSpeaker] || Manuel1} alt={mappedSpeaker} />
          <div className='module6-item-wrap'>
            <span className='bold'>{mappedSpeaker}: </span>
            {sentenceWithBlanks}
          </div>
        </div>
      );
      
      // Check if there are context lines to insert after this blank
      contextLines.forEach((contextLine, contextIndex) => {
        if (contextLine.position === index + 1 || (contextLine.position === "end" && index === blanks.length - 1)) {
          // Get the mapped character name for this speaker
          const contextMappedSpeaker = nameMapping[contextLine.speaker] || contextLine.speaker;
          
          dialogueElements.push(
            <div key={`context-${contextIndex}`} className='module6-item-container'>
              <img src={characterImages[contextMappedSpeaker] || Manuel1} alt={contextMappedSpeaker} />
              <div className='module6-item-wrap'>
                <span className='bold'>{contextMappedSpeaker}: </span>
                {contextLine.text}
              </div>
            </div>
          );
        }
      });
    });
    
    return dialogueElements;
  };

  return (
    <div className="module1-main module6">
      <div className="module1-wrap">
        <Progress number="06." percent={6 / 8} />
        <div className="module1-content-wrap module6-wrap">
          <div className="module1-logo">Module 6</div>
          <div className="module1-body">
            <div className="module1-title text-center">Wait... What Was That?</div>
            <div className="module6-content text-center">
              Overheard at dinner - Level {currentInstance.difficulty}: {currentInstance.dialogueTitle}
            </div>
            
            <div className='words-wrap-horizontal' style={{
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div className='words-title' style={{ marginBottom: '10px', fontWeight: 'bold' }}>Word Bank</div>
              <div className='words-list-horizontal' style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '15px'
              }}>
                {words.map((word, index) => (
                  <div key={index} className='words-content' style={{
                    padding: '5px 10px',
                    backgroundColor: 'white',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}>
                    {index + 1}. {word}
                  </div>
                ))}
              </div>
            </div>

            <div className='module6-content-wrap'>
              {renderDialogue()}
            </div>

            {submitted && (() => {
              const blanks = currentInstance.dialogue ? 
                currentInstance.dialogue.filter(line => line.hasBlank) : 
                currentInstance.blanks || [];
              const totalBlanks = blanks.length;
              const percentage = correctCount / totalBlanks;
              
              return (
                <div className="feedback-section" style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: percentage >= 0.8 ? '#d4edda' : '#f8d7da',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <h3>{correctCount}/{totalBlanks} Correct</h3>
                  <p>+{Math.round(percentage * currentInstance.difficulty)} points</p>
                  <p>{percentage >= 0.8 ? 'Moving to higher difficulty...' : 'Moving to lower difficulty...'}</p>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      <div className='next-btn-group'>
        <div className='help-btn-group'>
          <div className='help-btn-effect1'></div>
          <div className='help-btn-effect2'></div>
          <img src={HelpBtn} onClick={() => showModal()} />
          <div className='help-content'>Do you need help?</div>
        </div>

        {!submitted ? (
          <div onClick={nextStep} className='assessment-next-btn'>
            <span className='next-title'>Submit</span>
            <FaChevronRight />
          </div>
        ) : null}
      </div>

      <Modal
        open={open}
        onOk={() => showModal()}
        onCancel={() => hideModal()}
        closeIcon={<img src={Close} />}
        footer={null}
      >
        <div className="direction-modal-container">
          <div className='direction-modal-title'>Directions</div>
          <div className='direction-modal-content'>
            Fill in the blanks using words from the word bank. Each blank corresponds to a missing word in the conversation. 
            Choose the word that best completes each sentence based on context and grammar.
          </div>
        </div>
      </Modal>
    </div>
  )
}